import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
  SuperAdminDocumentEntry,
  SuperAdminDocumentType,
} from "@victor/types";
import { api } from "../../../lib/api";
import { Input } from "../../../ui/Input";
import { Tabs } from "../../../ui/Tabs";
import { PageSpinner } from "../../../ui/Spinner";
import { Badge, EmptyInline, Panel, fmtDate, fmtMoney } from "./_ui";

type Props = { tenantId: string };

const TYPES: ReadonlyArray<{ key: SuperAdminDocumentType; label: string }> = [
  { key: "contrat", label: "Contrats" },
  { key: "devis", label: "Devis" },
  { key: "facture", label: "Factures" },
  { key: "acompte", label: "Acomptes" },
];

export function DocumentsTab({ tenantId }: Props) {
  const [type, setType] = useState<SuperAdminDocumentType>("facture");
  const [q, setQ] = useState("");

  const docsQ = useQuery({
    queryKey: ["tenant-documents", tenantId, type, q],
    queryFn: () =>
      api.documents.list(tenantId, { type, q: q || undefined, limit: 200 }),
  });

  const items = docsQ.data?.items ?? [];

  return (
    <div className="space-y-4">
      <Tabs
        value={type}
        onChange={(k) => setType(k as SuperAdminDocumentType)}
        items={TYPES.map((t) => ({
          key: t.key,
          label: t.label,
          count: docsQ.data && type === t.key ? docsQ.data.total : undefined,
        }))}
        variant="segmented"
      />
      <Panel>
        <Input
          label="Recherche"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Numéro, objet…"
        />
      </Panel>
      {docsQ.isLoading ? (
        <PageSpinner />
      ) : (
        <Panel padded={false}>
          {items.length === 0 ? (
            <div className="p-5">
              <EmptyInline>Aucun document.</EmptyInline>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-100">
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                    Numéro
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                    Date
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                    Client
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-navy-500">
                    Total HT
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                    Statut
                  </th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {items.map((r: SuperAdminDocumentEntry) => (
                  <tr
                    key={`${r.type}-${r.id}`}
                    className="transition-colors hover:bg-navy-50/40"
                  >
                    <td className="px-5 py-3 font-bold text-navy-800">
                      {r.numero}
                    </td>
                    <td className="px-5 py-3 text-xs text-navy-600">
                      {fmtDate(r.date)}
                    </td>
                    <td className="px-5 py-3 text-navy-700">{r.clientNom}</td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {r.montantHt != null ? (
                        <span className="font-semibold text-navy-800">
                          {fmtMoney(r.montantHt)} €
                        </span>
                      ) : (
                        <span className="text-navy-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone="navy">{r.statut}</Badge>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {r.hasPdf ? (
                        <a
                          href={api.documents.pdfUrl(tenantId, r.type, r.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-bold text-accent-500 hover:underline"
                        >
                          <PdfIcon /> PDF
                        </a>
                      ) : (
                        <span className="text-xs text-navy-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}
    </div>
  );
}

function PdfIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="14" x2="15" y2="14" />
    </svg>
  );
}
